export interface EmailProgramInfo {
  name: string;
  term: string;
}

export interface EmailStudentInfo {
  id: string | number;
  fullName: string;
  login: string;
  password: string;
  programs: EmailProgramInfo[];
}

export interface EmailGenerationParams {
  recipientEmail: string;
  recipientName: string;
  recipientRole: 'student' | 'specialist';
  introText: string;
  studentsData: EmailStudentInfo[];
}

const LOGO_URL = 'https://xn----ftbfngljzv6a8e3a.xn--p1ai/img/logo_w.svg?v=1';

const FOOTER_INFO_HTML = `
<p style="font-size: 14px; font-weight: bold; margin: 20px 0 10px 0;">Важная информация:</p>
<p style="font-size: 14px; margin: 5px 0;">&bull; Сдача экзамена досрочно запрещена, результат будет считаться недействительным.</p>
<p style="font-size: 14px; margin: 5px 0;">&bull; Система дистанционного обучения имеет ограничение на сдачу итогового тестирования — 2 раза в сутки.</p>
<p style="font-size: 14px; margin: 5px 0;">&bull; Если появляется ошибка «Неверный логин/пароль» — проверьте правильность ввода. Если ошибка сохраняется, значит срок доступа истёк.</p>
<p style="font-size: 14px; margin: 5px 0;">&bull; По вопросам продления доступа обращайтесь в соответствующий отдел обучения.</p>
<p style="font-size: 14px; margin: 20px 0 5px 0;">АНО ДПО УМКЦ "Энергия", отдел дистанционного обучения</p>
<p style="font-size: 14px; margin: 5px 0;">185030, Республика Карелия, г. Петрозаводск, пр. Александра Невского, д.68<br>8 (8142) 59-35-79<br><a href="https://умкц-энергия.рф" title="Перейти на сайт УМКЦ Энергия" style="color: #3498db; text-decoration: none;">https://умкц-энергия.рф</a></p>
`;

const HEADER_YANDEX = `<tr><td style="padding: 16px 24px; background: linear-gradient(135deg, #39495c, #3498db); text-align: center;"><img src="${LOGO_URL}" alt="SDO-ENERGY.RU" border="0" style="height: 70px; display: block; margin: 0 auto;" /></td></tr>`;

const HEADER_STANDARD = `<tr><td style="padding:24px 24px 18px; text-align:center; background:linear-gradient(135deg,#39495c,#3498db);"><table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0"><tr><td align="center"><div class="e-title" style="font-family:Arial, Helvetica, sans-serif; font-style:italic; font-weight:700; font-size:51px; line-height:0.8; letter-spacing:5px; text-transform:uppercase; color:#ffffff; transform:scaleY(0.93);">ЭНЕРГИЯ</div></td></tr><tr><td align="center"><div style="height:1px; background-color:#ffffff; max-width:560px; width:50%; margin:1px auto 8px;"></div></td></tr><tr><td align="center"><div class="e-subtitle" style="font-family:Arial, Helvetica, sans-serif; font-style:italic; font-weight:600; font-size:8.5px; line-height:1; letter-spacing:1.2px; color:#ffffff;">УЧЕБНО-МЕТОДИЧЕСКИЙ&nbsp;КОНСАЛТИНГОВЫЙ&nbsp;ЦЕНТР</div></td></tr></table></td></tr>`;

export const generateEmailHtml = (params: EmailGenerationParams): string => {
  const { recipientEmail, recipientName, recipientRole, introText, studentsData } = params;

  const domain = recipientEmail.split('@')[1]?.toLowerCase() || '';
  const headerHtml = domain.includes("yandex") ? HEADER_YANDEX : HEADER_STANDARD;

  let tableRows = '';
  const cellStyle = 'border: 1px solid #e9ecef; padding: 10px; text-align: left; vertical-align: top;';

  studentsData.forEach(student => {
      const iterations = student.programs.length > 0
          ? student.programs
          : [{ name: '—', term: '' }];

      const rowSpan = iterations.length;

      iterations.forEach((prog, idx) => {
          tableRows += '<tr>';
          if (idx === 0) {
              tableRows += `<td rowspan="${rowSpan}" style="${cellStyle}">${student.fullName}</td>`;
              tableRows += `<td style="${cellStyle}">${prog.name}</td>`;
              tableRows += `<td style="${cellStyle}">${prog.term}</td>`;
              tableRows += `<td rowspan="${rowSpan}" style="${cellStyle}">${student.login}</td>`;
              tableRows += `<td rowspan="${rowSpan}" style="${cellStyle}">${student.password}</td>`;
          } else {
              tableRows += `<td style="${cellStyle}">${prog.name}</td>`;
              tableRows += `<td style="${cellStyle}">${prog.term}</td>`;
          }
          tableRows += '</tr>';
      });
  });

  const tableHeaderStyle = 'border: 1px solid #e9ecef; padding: 10px; background-color: #f8f9fa; font-size: 12px; text-transform: uppercase; letter-spacing: 0.4px; text-align: left; vertical-align: top;';

  let title = "Данные для входа";
  if (recipientRole === 'specialist') {
      title = "Сводные данные для входа (заявка)";
  }

  const finalIntro = introText;

  const preTableTitle = recipientRole === 'student'
      ? "Ниже приведены сведения по слушателю."
      : "Ниже приведены сведения по слушателям.";

  const bodyHtml = `
      <p style="font-size: 13px; color: #6c757d; margin: 0 0 16px 0;">${preTableTitle}</p>
      <div style="overflow-x: auto; width: 100%; -webkit-overflow-scrolling: touch;">
          <table border="0" cellspacing="0" cellpadding="0" role="presentation" style="width: 100%; border-collapse: collapse; font-size: 14px; min-width: 480px;">
              <thead>
                  <tr>
                      <th style="${tableHeaderStyle}">ФИО</th>
                      <th style="${tableHeaderStyle}">Программа</th>
                      <th style="${tableHeaderStyle}">Срок подключения</th>
                      <th style="${tableHeaderStyle}">Логин</th>
                      <th style="${tableHeaderStyle}">Пароль</th>
                  </tr>
              </thead>
              <tbody>
                  ${tableRows}
              </tbody>
          </table>
      </div>
      ${FOOTER_INFO_HTML}
  `;

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="ru">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
</head>
<body style="margin: 0; padding: 0; background-color: #f5f6f8; font-family: Arial, Helvetica, sans-serif; color: #2c3e50;">
<div style="font-size: 0px; color: #f5f6f8; line-height: 1px; mso-line-height-rule: exactly; display: none; max-height: 0px; max-width: 0px; opacity: 0; overflow: hidden; mso-hide: all;">
${title}&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&zwnj;&nbsp;&zwnj;&nbsp;
</div>
<table align="center" border="0" cellspacing="0" cellpadding="0" role="presentation" style="width: 100%; max-width: 900px; margin: 0 auto; background-color: #ffffff;">
  ${headerHtml}
  <tr>
      <td style="padding: 24px;">
          <p style="font-size: 18px; font-weight: bold; margin: 0 0 12px 0;">${title}</p>
          <div style="font-size: 13px; color: #6c757d; margin: 0 0 16px 0;">${finalIntro}</div>
          ${bodyHtml}
      </td>
  </tr>
  <tr>
      <td style="padding: 16px 24px; background-color: #f8f9fa; text-align: center; font-size: 12px; color: #6c757d;">
          Это письмо сформировано автоматически. Пожалуйста, не отвечайте на него.
      </td>
  </tr>
</table>
<!-- SPACER DIV: Важный элемент. Создает пустое место внизу страницы, чтобы кнопки поверх контента не закрывали текст в самом низу -->
<div style="height: 120px; width: 100%; clear: both;"></div>
</body>
</html>`;
};